import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

const zodAddressSchema = z.object({
  buildingNumber: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  postCode: z.string().optional(),
  streetName: z.string().optional(),
});

const zodCreateSiteIdentifierPayload = z
  .object({
    address: zodAddressSchema.optional(),
    blacklisted: z.boolean().optional(),
    identifier: z.string().min(1),
    ignoreAddressInfo: z.boolean(),
    siteId: z.uuid(),
    supplierId: z.uuid(),
  })
  .refine(
    (value) => {
      if (value.ignoreAddressInfo) {
        return value.address === undefined;
      }

      return true;
    },
    {
      error: 'There should be no address if address should be ignored',
    },
  );

export type UpdateSiteIdentifierPayload = z.infer<
  typeof zodCreateSiteIdentifierPayload
>;

/**
 * Updates the site identifier for a given site based on the provided data for the identifier.
 * Allows blacklisting the site identifier with attached sites.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/create_site_identifier_site_identifier_post
 *
 * @param payload - The object containing data to update the site identifier.
 * @param payload.supplierId - The ID of the supplier.
 * @param payload.siteId - The ID of the site.
 * @param payload.identifier - The identifier to be created.
 * @param payload.ignoreAddressInfo - Whether to ignore the address information.
 * @param payload.address - The address object.
 * @param payload.blacklisted - Whether the site identifier should be blacklisted.
 *
 * @return The ID of the newly created site identifier.
 */

export const createSiteIdentifier = async (
  payload: UpdateSiteIdentifierPayload,
) => {
  payload.address &&= withoutObjectKeysWhereValueIs(payload.address, [
    undefined,
    null,
    '',
  ]);

  const response = await vestigasApi
    .post(ENDPOINT.SITE.UPDATE_IDENTIFIER(), {
      json: payload,
      validate: {
        body: zodCreateSiteIdentifierPayload,
      },
    })
    .json<{ id: UUID }>();

  return response;
};

/**
 * Custom hook for updating the identifier of a given site.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationCreateSiteIdentifier = (
  options?: Parameters<typeof useMutation>[0],
) => {
  return useMutation({
    mutationFn: async (payload: UpdateSiteIdentifierPayload) =>
      createSiteIdentifier(payload),
    ...options,
  });
};
